import { FC, useContext, useEffect, useRef, useState } from "react";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../context/notificationContext";
import { useUserSettings } from "../../context/userSettingsContext";
import useDataInitializer from "../../hooks/useDataInitializer";
import { useI18n } from "../../hooks/useTranslations";
import { useCarSubsidiariesApi } from "../../http/carSubsidiaries/api";
import { UpdateCarSubsidiary } from "../../http/carSubsidiaries/types";
import ModalDialog from "../ui/ModalDialog";
import CarSubsidiaryForm, { CarSubsidiaryFormBody } from "./CarSubsidiaryForm";

type EditCarSubsidiaryDialogProps = {
  carSubsidiaryId: string;
  onClose: (success?: boolean) => void;
  onUpdate: () => void;
};

const EditCarSubsidiaryDialog: FC<EditCarSubsidiaryDialogProps> = ({
  carSubsidiaryId,
  onClose,
  onUpdate,
}: EditCarSubsidiaryDialogProps) => {
  const i18n = useI18n();
  const aborter = useRef(new AbortController());
  const dispatch = useContext(NotificationDispatch);
  const userSettings = useUserSettings();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const carSubsidiariesApi = useCarSubsidiariesApi();

  const carSubsidiarySwr = carSubsidiariesApi.getCarSubsidiary(carSubsidiaryId, aborter.current.signal);
  const [carSubsidiary, isInitialized] = useDataInitializer(carSubsidiarySwr.data, carSubsidiarySwr.error);

  useEffect(() => {
    carSubsidiaryId && carSubsidiarySwr.trigger();
  }, [carSubsidiaryId]);

  useEffect(() => {
    carSubsidiarySwr.error && dispatch(showErrorNotification(i18n, carSubsidiarySwr.error));
  }, [carSubsidiarySwr.error]);

  const onSubmit = async (formData: CarSubsidiaryFormBody): Promise<void> => {
    if (carSubsidiary?.id) {
      const body: UpdateCarSubsidiary = {
        subsidiaryId: formData.subsidiary.id,
        startDate: formData.startDate,
        endDate: formData.endDate,
        currentVersionNumber: carSubsidiarySwr.data?.versionNumber,
        closeOpenEntities: false,
      };

      try {
        await carSubsidiariesApi.updateCarSubsidiary(carSubsidiary.id, body, carSubsidiary.car.id);
        dispatch(showSuccessNotification(i18n.t));
        onClose(true);
      } catch (error) {
        dispatch(showErrorNotification(i18n, error));
      } finally {
        onUpdate();
        setIsSubmitting(false);
      }
    }
  };

  return (
    <ModalDialog onClose={onClose} title={i18n.t.carSubsidiaries.edit}>
      <CarSubsidiaryForm
        onSubmit={onSubmit}
        isDisabled={isSubmitting || !userSettings.permissions.cars.update || !carSubsidiary?.id}
        isLoading={!isInitialized}
        carSubsidiary={carSubsidiary}
        onCloseEntitiesDialog={() => {
          onUpdate();
          onClose();
        }}
      />
    </ModalDialog>
  );
};

export default EditCarSubsidiaryDialog;
